import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { Plus, Trash2, IndianRupee, Printer, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { printInvoice } from "@/lib/printing";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);

const statusChip = {
  paid: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  partial: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  unpaid: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

const emptyForm = () => ({
  patient_id: "",
  items: [{ description: "Consultation", qty: 1, unit_price: 500, gst_rate: 18 }],
  discount: 0,
  amount_paid: 0,
  payment_mode: "cash",
  note: "",
});

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating new invoice
  const [payOpen, setPayOpen] = useState(null); // invoice id
  const [payAmt, setPayAmt] = useState(0);
  const [payMode, setPayMode] = useState("cash");

  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [inv, pts] = await Promise.all([api.get("/invoices"), api.get("/patients")]);
    setInvoices(inv.data); setPatients(pts.data);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    let sub = 0, gst = 0;
    form.items.forEach((it) => {
      const line = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
      sub += line;
      gst += line * ((Number(it.gst_rate) || 0) / 100);
    });
    const total = Math.max(0, sub + gst - Number(form.discount || 0));
    const balance = total - Number(form.amount_paid || 0);
    return { sub, gst, total, balance };
  }, [form]);

  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", qty: 1, unit_price: 0, gst_rate: 18 }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (inv) => {
    setEditingId(inv.id);
    setForm({
      patient_id: inv.patient_id,
      items: inv.items.map((it) => ({ ...it })),
      discount: inv.discount || 0,
      amount_paid: inv.amount_paid || 0,
      payment_mode: inv.payment_mode || "cash",
      note: inv.note || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error("Select a patient");
    try {
      const items = form.items.map((it) => ({ ...it, qty: Number(it.qty), unit_price: Number(it.unit_price), gst_rate: Number(it.gst_rate) }));
      if (editingId) {
        await api.put(`/invoices/${editingId}`, {
          items, discount: Number(form.discount || 0), payment_mode: form.payment_mode, note: form.note,
        });
        toast.success("Invoice updated");
      } else {
        const payload = { ...form, items, discount: Number(form.discount || 0), amount_paid: Number(form.amount_paid || 0) };
        await api.post("/invoices", payload);
        toast.success("Invoice created");
      }
      setOpen(false);
      setForm(emptyForm());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const finalizeInvoice = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/finalize`);
      const n = data.notifications;
      if (n?.upi_configured) {
        const parts = [];
        parts.push(n.email_sent ? "emailed" : "email not sent");
        parts.push(n.whatsapp_sent ? "WhatsApp sent" : "WhatsApp not sent");
        toast.success(`Invoice finalized · ${parts.join(" · ")}`);
      } else {
        toast.success("Invoice finalized");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyPayment = async () => {
    try {
      await api.post(`/invoices/${payOpen}/payment`, { amount: Number(payAmt), mode: payMode });
      toast.success("Payment recorded");
      setPayOpen(null); setPayAmt(0);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const label = "text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block";
  const inp = "w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A]";

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold mb-1">Financials</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Billing</h1>
          <p className="text-sm text-[#52525B] mt-1">{invoices.length} invoices · GST-compliant · part-payment ready</p>
        </div>
        <button data-testid="new-invoice-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#16654F]">
          <Plus className="w-4 h-4" /> New invoice
        </button>
      </div>

      <div className="bg-white border border-[#E5E5E2] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#52525B] bg-[#FDFDFB]">
              <th className="p-3 border-b border-[#E5E5E2]">Invoice</th>
              <th className="p-3 border-b border-[#E5E5E2]">Patient</th>
              <th className="p-3 border-b border-[#E5E5E2] text-right">Subtotal</th>
              <th className="p-3 border-b border-[#E5E5E2] text-right">GST</th>
              <th className="p-3 border-b border-[#E5E5E2] text-right">Total</th>
              <th className="p-3 border-b border-[#E5E5E2] text-right">Balance</th>
              <th className="p-3 border-b border-[#E5E5E2]">Status</th>
              <th className="p-3 border-b border-[#E5E5E2]"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={8} className="p-10 text-center text-[#52525B]">No invoices yet.</td></tr>
            )}
            {invoices.map((i) => (
              <tr key={i.id} className="hover:bg-[#FDFDFB]" data-testid={`invoice-row-${i.invoice_no}`}>
                <td className="p-3 border-b border-[#E5E5E2] font-mono-num text-[#52525B]">{i.invoice_no}</td>
                <td className="p-3 border-b border-[#E5E5E2] font-medium">{i.patient_name}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-right font-mono-num">{fmt(i.subtotal)}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-right font-mono-num text-[#52525B]">{fmt(i.gst_total)}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-right font-mono-num font-semibold">{fmt(i.total)}</td>
                <td className="p-3 border-b border-[#E5E5E2] text-right font-mono-num">{fmt(i.balance)}</td>
                <td className="p-3 border-b border-[#E5E5E2]">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] uppercase tracking-wider font-semibold border px-2 py-1 rounded-sm ${statusChip[i.status]}`}>{i.status}</span>
                    {i.is_draft && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold border px-2 py-1 rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" data-testid={`draft-chip-${i.invoice_no}`}>Draft</span>
                    )}
                  </div>
                </td>
                <td className="p-3 border-b border-[#E5E5E2] text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => { const pat = patients.find((p) => p.id === i.patient_id) || { full_name: i.patient_name, mrn: "", address: "" }; printInvoice(i, pat); }} className="text-xs px-2 py-1.5 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1" data-testid={`print-invoice-${i.invoice_no}`}>
                      <Printer className="w-3 h-3" /> Print
                    </button>
                    {i.is_draft && (
                      <button onClick={() => openEdit(i)} data-testid={`edit-invoice-${i.invoice_no}`} className="text-xs px-2 py-1.5 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1">
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                    )}
                    {i.is_draft && (
                      <button onClick={() => finalizeInvoice(i.id)} data-testid={`finalize-invoice-${i.invoice_no}`} className="text-xs px-3 py-1.5 bg-[#E27B58] text-white rounded-sm hover:bg-[#c96843] inline-flex items-center gap-1 font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Finalize
                      </button>
                    )}
                    {i.status !== "paid" && (
                      <button onClick={() => { setPayOpen(i.id); setPayAmt(i.balance); }} data-testid={`pay-btn-${i.invoice_no}`} className="text-xs px-3 py-1.5 bg-[#0F4C3A] text-white rounded-sm hover:bg-[#16654F]">
                        Record payment
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-invoice-modal">
          <form onSubmit={submit} className="w-full max-w-3xl bg-white rounded-md shadow-2xl max-h-[90vh] overflow-auto">
            <div className="px-6 py-4 border-b border-[#E5E5E2] flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="font-display text-lg font-bold tracking-tight">{editingId ? "Edit invoice (draft)" : "New invoice"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#52525B]">Close</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={label}>Patient *</label>
                <select data-testid="invoice-patient" required disabled={!!editingId} className={`${inp} ${editingId ? "bg-[#F4F4F5] text-[#52525B]" : ""}`} value={form.patient_id} onChange={(e) => setForm((f) => ({ ...f, patient_id: e.target.value }))}>
                  <option value="">Select…</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name} · {p.mrn}</option>)}
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <div className={label + " mb-0"}>Line items</div>
                  <button type="button" onClick={addItem} className="text-xs px-2 py-1 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1" data-testid="add-item-btn"><Plus className="w-3 h-3" /> Add</button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input placeholder="Description" className={`${inp} col-span-5`} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`item-desc-${i}`} />
                      <input type="number" min="1" placeholder="Qty" className={`${inp} col-span-1`} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                      <input type="number" min="0" placeholder="Unit ₹" className={`${inp} col-span-2`} value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`item-price-${i}`} />
                      <input type="number" min="0" step="0.1" placeholder="GST %" className={`${inp} col-span-2`} value={it.gst_rate} onChange={(e) => setItem(i, "gst_rate", e.target.value)} />
                      <div className="col-span-1 text-right text-xs font-mono-num text-[#52525B]">
                        {fmt((Number(it.qty)||0) * (Number(it.unit_price)||0))}
                      </div>
                      <button type="button" onClick={() => removeItem(i)} className="col-span-1 text-[#D93838] hover:bg-[#FEE2E2] rounded-sm p-2 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className={`grid ${editingId ? "grid-cols-3" : "grid-cols-4"} gap-3`}>
                <div><label className={label}>Discount ₹</label><input type="number" min="0" className={inp} value={form.discount} onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))} /></div>
                {!editingId && (
                  <div><label className={label}>Amount paid ₹</label><input type="number" min="0" className={inp} value={form.amount_paid} onChange={(e) => setForm((f) => ({ ...f, amount_paid: e.target.value }))} data-testid="invoice-paid" /></div>
                )}
                <div>
                  <label className={label}>Payment mode</label>
                  <select className={inp} value={form.payment_mode} onChange={(e) => setForm((f) => ({ ...f, payment_mode: e.target.value }))}>
                    {["cash","upi","card","netbanking","pending"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                  </select>
                </div>
                <div><label className={label}>Note</label><input className={inp} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></div>
              </div>

              <div className="bg-[#FDFDFB] border border-[#E5E5E2] rounded-md p-4 grid grid-cols-4 gap-2 text-sm font-mono-num">
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">Subtotal</div>{fmt(totals.sub)}</div>
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">GST</div>{fmt(totals.gst)}</div>
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">Total</div><span className="font-bold text-base">{fmt(totals.total)}</span></div>
                <div><div className="text-xs uppercase tracking-wider text-[#52525B]">Balance</div><span className={totals.balance > 0 ? "text-[#991B1B]" : "text-[#166534]"}>{fmt(totals.balance)}</span></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#E5E5E2] flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md">Cancel</button>
              <button type="submit" data-testid="save-invoice-btn" className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F] inline-flex items-center gap-1">
                <IndianRupee className="w-4 h-4" /> {editingId ? "Save changes" : "Create invoice"}
              </button>
            </div>
          </form>
        </div>
      )}

      {payOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pay-modal">
          <div className="w-full max-w-sm bg-white rounded-md shadow-2xl p-6">
            <h3 className="font-display text-lg font-bold tracking-tight mb-4">Record payment</h3>
            <label className={label}>Amount ₹</label>
            <input data-testid="pay-amount" type="number" min="0" className={inp} value={payAmt} onChange={(e) => setPayAmt(e.target.value)} />
            <label className={`${label} mt-3`}>Mode</label>
            <select className={inp} value={payMode} onChange={(e) => setPayMode(e.target.value)}>
              {["cash","upi","card","netbanking"].map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
            </select>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setPayOpen(null)} className="text-sm px-4 py-2 border border-[#E5E5E2] rounded-md">Cancel</button>
              <button onClick={applyPayment} data-testid="confirm-pay-btn" className="text-sm px-4 py-2 bg-[#0F4C3A] text-white rounded-md hover:bg-[#16654F]">Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
